import React, {
    createContext,
    PropsWithChildren,
    useContext,
    useState
} from "react";
import { AdvancedStudyLoad } from "types/advancedStudyType";
import { NiraBusId } from "types/busType";

type AdvancedStudyContextType = {
    studyTitle: string;
    setStudyTitle: (title: string) => void;
    studyBuses: AdvancedStudyLoad[];
    addBus: (bus: AdvancedStudyLoad) => void;
    removeBus: (niraBusId: NiraBusId) => void;
    updateBusAndMaybeRemove: (bus: AdvancedStudyLoad) => void;
    resetStudy: () => void;
    isNewStudyDialogOpen: boolean;
    openNewStudyDialog: () => void;
    closeNewStudyDialog: () => void;
    isAddingMoreLoad: boolean;
    enterAddMoreLoadMode: () => void;
    exitAddMoreLoadMode: () => void;
};

const AdvancedStudyContext = createContext<
    AdvancedStudyContextType | undefined
>(undefined);

export const AdvancedStudyProvider: React.FC<PropsWithChildren> = ({
    children
}: PropsWithChildren) => {
    const [studyTitle, setStudyTitle] = useState<string>("");
    const [studyBuses, setStudyBuses] = useState<AdvancedStudyLoad[]>([]);
    const [isNewStudyDialogOpen, setIsNewStudyDialogOpen] =
        useState<boolean>(false);
    const [isAddingMoreLoad, setIsAddingMoreLoad] = useState<boolean>(false);

    const addBus = (bus: AdvancedStudyLoad) => {
        setStudyBuses((prevBuses) => {
            const exists = prevBuses.some((b) => b.niraBusId === bus.niraBusId);
            if (exists) return prevBuses;
            return [...prevBuses, bus];
        });
    };

    const removeBus = (niraBusId: NiraBusId) => {
        setStudyBuses((prevBuses) =>
            prevBuses.filter((bus) => bus.niraBusId !== niraBusId)
        );
    };

    const updateBusAndMaybeRemove = (updatedBus: AdvancedStudyLoad) => {
        if (
            updatedBus.studyMwSize === undefined &&
            updatedBus.benchMwSize === undefined
        ) {
            removeBus(updatedBus.niraBusId);
            return;
        }

        setStudyBuses((prevBuses) =>
            prevBuses.map((b) =>
                b.niraBusId === updatedBus.niraBusId ? updatedBus : b
            )
        );
    };

    const resetStudy = () => {
        setStudyTitle("");
        setStudyBuses([]);
    };

    const openNewStudyDialog = () => {
        setIsNewStudyDialogOpen(true);
        setIsAddingMoreLoad(false);
    };

    const closeNewStudyDialog = () => {
        setIsNewStudyDialogOpen(false);
    };

    const enterAddMoreLoadMode = () => {
        setIsAddingMoreLoad(true);
        setIsNewStudyDialogOpen(false);
    };

    const exitAddMoreLoadMode = () => {
        setIsAddingMoreLoad(false);
    };

    const contextValue: AdvancedStudyContextType = {
        studyTitle,
        setStudyTitle,
        studyBuses,
        addBus,
        removeBus,
        updateBusAndMaybeRemove,
        resetStudy,
        isNewStudyDialogOpen,
        openNewStudyDialog,
        closeNewStudyDialog,
        isAddingMoreLoad,
        enterAddMoreLoadMode,
        exitAddMoreLoadMode
    };

    return (
        <AdvancedStudyContext.Provider value={contextValue}>
            {children}
        </AdvancedStudyContext.Provider>
    );
};

export const useAdvancedStudyContext = (): AdvancedStudyContextType => {
    const context = useContext(AdvancedStudyContext);
    if (!context) {
        throw new Error(
            "useAdvancedStudyContext must be used within an AdvancedStudyProvider"
        );
    }
    return context;
};
